package com.arenaproplay.admin.ui.components.overlay

import androidx.compose.runtime.Composable
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

/**
 * Shared confirm/danger dialog overlay.
 *
 * Preferred over extending [Modal] because Modal's `content` lambda is
 * generic — expressing title/description/warning/error/busy/danger-button
 * in a typed, reusable way requires either a separate composable or heavy
 * default-parameter soup; a dedicated file keeps the contract explicit and
 * lets callers stay free of layout boilerplate.
 *
 * Usage:
 * ```
 * ConfirmActionOverlay(
 *     isOpen = pendingAction != null,
 *     onClose = { pendingAction = null },
 *     title = "Удалить пользователя",
 *     description = "…",
 *     confirmDanger = true,
 *     confirmLabel = "Удалить",
 *     onConfirm = { /* perform action */ }
 * )
 * ```
 */
@Composable
fun ConfirmActionOverlay(
    isOpen: Boolean,
    onClose: () -> Unit,
    title: String,
    description: String,
    warning: String? = null,
    error: String? = null,
    confirmLabel: String,
    confirmDanger: Boolean = false,
    confirmEnabled: Boolean = true,
    onConfirm: () -> Unit,
) {
    if (!isOpen) return

    // Scrim — derived from AppColors.Ink at 40 % opacity
    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(600)
            .styleModifier {
                property("background", AppColors.Ink.toRgbaString(0.40f))
                property("backdrop-filter", "blur(2px)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .onClick { onClose() }
    ) {
        // Dialog panel
        Box(
            Modifier
                .sBorderRadius(16)
                .styleModifier {
                    property("background", AppColors.Surface.toRgbaString(1f))
                    property(
                        "box-shadow",
                        "0 8px 40px -8px ${AppColors.Ink.toRgbaString(0.24f)}, " +
                            "0 2px 8px ${AppColors.Ink.toRgbaString(0.08f)}"
                    )
                    property("padding", "28px")
                    property("max-width", "440px")
                    property("width", "calc(100vw - 48px)")
                    property("position", "relative")
                }
                .onClick { it.stopPropagation() }
        ) {
            Column(Modifier.fillMaxWidth()) {
                SpanText(
                    title,
                    Modifier.bodyMMedium().color(AppColors.Ink)
                        .styleModifier {
                            property("font-size", "16px")
                            property("font-weight", "700")
                        }
                )
                Box(Modifier.height(10.px))
                SpanText(description, Modifier.bodyMRegular().color(AppColors.Muted))

                if (warning != null) {
                    Box(Modifier.height(10.px))
                    Box(
                        Modifier.fillMaxWidth().sBorderRadius(8).sPadding(10)
                            .styleModifier {
                                property("background", AppColors.Accent.WarningTint.toRgbaString(1f))
                                property("border", "1px solid ${AppColors.Warning.toRgbaString(0.22f)}")
                            }
                    ) {
                        SpanText(warning, Modifier.bodySMedium().color(AppColors.Warning))
                    }
                }

                if (error != null) {
                    Box(Modifier.height(10.px))
                    SpanText(error, Modifier.bodySMedium().color(AppColors.Error))
                }

                Box(Modifier.height(20.px))

                Row(
                    Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(Modifier.weight(1f))
                    // Cancel button
                    Box(
                        Modifier
                            .sBorderRadius(999)
                            .padding(leftRight = 18.px, topBottom = 9.px)
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property("background", AppColors.Surface.toRgbaString(1f))
                                property("border", "1px solid ${AppColors.Ink.toRgbaString(0.10f)}")
                            }
                            .onClick { onClose() }
                    ) {
                        SpanText(
                            "Отмена",
                            Modifier.bodySMedium().color(AppColors.Ink)
                                .styleModifier { property("font-weight", "600") }
                        )
                    }
                    Box(Modifier.width(8.px))
                    // Confirm button — danger = Error token, normal = Ink token
                    val confirmBg = if (confirmDanger) AppColors.Error else AppColors.Ink
                    Box(
                        Modifier
                            .sBorderRadius(999)
                            .padding(leftRight = 18.px, topBottom = 9.px)
                            .cursor(if (confirmEnabled) Cursor.Pointer else Cursor.NotAllowed)
                            .styleModifier {
                                property("background", confirmBg.toRgbaString(1f))
                                if (!confirmEnabled) property("opacity", "0.5")
                            }
                            .onClick { if (confirmEnabled) onConfirm() }
                    ) {
                        SpanText(
                            confirmLabel,
                            Modifier.bodySMedium().color(AppColors.Surface)
                                .styleModifier { property("font-weight", "600") }
                        )
                    }
                }
            }
        }
    }
}
